/**
 * The period of a pharmacy's copay table: 'quarter' or 'month'.
 *
 * The columns are the pharmacy's estimated_monthly_costs from Medicare.gov,
 * which picks the period from the fills there. At mail order it converts a
 * drug to a 90-day fill where the plan's tier offers one; when every drug is
 * then a 90-day fill the dates come back quarterly (Jan/Apr/Jul/Oct), and a
 * single 30-day-only drug (e.g. a Tier 4 with no 90-day supply) makes them
 * monthly. Checked live 2026-10-05 on Humana Basic Rx 2027. So the labels
 * follow the dates, not the mail-order flag.
 */
const monthIndex = (date) => {
    const [y, m] = String(date || '').split('-').map(n => parseInt(n, 10))
    return y * 12 + m
}

export function tablePeriod (pharmacy) {
    const dates = (pharmacy?.estimated_monthly_costs || []).map(mc => mc.date)
    if (dates.length >= 2) {
        // a later effective date trims the table but keeps the spacing
        return dates.slice(1).every((d, i) => monthIndex(d) - monthIndex(dates[i]) === 3) ? 'quarter' : 'month'
    }
    // one column (or none) cannot show its spacing: read the fills instead
    const fills = pharmacy?.drug_costs || []
    return fills.length && fills.every(dc => dc.frequency === 'FREQUENCY_90_DAYS') ? 'quarter' : 'month'
}

/**
 * The month the table marks as the start of the catastrophic phase, or null.
 *
 * Medicare.gov dates the phase from the drugs' full cost, so it can land
 * before the client has paid anything: a $0-copay Medicare Advantage plan at
 * a pharmacy with high full prices is "catastrophic" by July while every cell
 * is $0 (client 1985, 2027; 52 such rows in the prod quote cache on
 * 2026-10-06). The marker only shows once the client has paid something by
 * that month: oracle's per-drug table, else Medicare.gov's monthly totals.
 */
export function catastrophicMarkerDate (pharmacy) {
    const date = pharmacy?.phase_information?.catastrophic_start
    const months = pharmacy?.estimated_monthly_costs || []
    const idx = date ? months.findIndex(mc => mc.date === date) : -1
    if (idx < 0) {
        return null
    }
    const table = pharmacy.estimated_monthly_costs_per_drug_table
    const monthPaid = table
        ? row => (row || []).reduce((sum, v) => sum + (v || 0), 0)
        : (row, i) => months[i].total || 0
    const rows = table || months
    const paid = rows.slice(0, idx + 1).reduce((sum, row, i) => sum + monthPaid(row, i), 0)
    return paid > 0.005 ? date : null
}
